import { CurrencyOptionResource, CurrencyTypeProvider, FormattedCurrency, FormattedDate } from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { HotelSummaryPriceResource } from "../../../shared/types/hotel";

interface Props {
	currency: CurrencyOptionResource | null;
	prices: HotelSummaryPriceResource[];
	total: number;
	addedAdditionalPersons: boolean;
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	padding: ${({ theme }) => theme.spacing(4)};
	gap: ${({ theme }) => theme.spacing(2)};
`;

const RequestedNightsGroups = styled.div`
	display: grid;
	flex: 1;
	grid-template-columns: 1fr;

	${({ theme }) => theme.breakpoints.tablet} {
		gap: ${({ theme }) => theme.spacing(6)};
		grid-template-columns: 1fr 1fr;
	}
`;

const RequestedNightPrice = styled.div`
	padding-left: ${({ theme }) => theme.spacing(4)};
	white-space: nowrap;
`;

const RequestedNightsList = styled.ul`
	list-style: none;

	& > li {
		display: flex;
		justify-content: space-between;
	}
`;

const TotalPrice = styled.div`
	display: flex;
	justify-content: space-between;
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

export const HotelSummaryRequestedNights: FC<Props> = ({ currency, prices, total, addedAdditionalPersons }) => {
	const { t } = useTranslation();
	const pricesGroupOf7 = useMemo(() => {
		const grouped: HotelSummaryPriceResource[][] = [];
		for (let i = 0; i < prices.length; i += 7) {
			grouped.push(prices.slice(i, i + 7));
		}
		return grouped;
	}, [prices]);

	return (
		<CurrencyTypeProvider currencyType={currency ?? undefined}>
			<Container>
				<strong>{t("Requested nights")}:</strong>
				<RequestedNightsGroups>
					{pricesGroupOf7.map((priceGroup, groupIndex) => (
						<RequestedNightsList key={groupIndex}>
							{priceGroup.map((price, index) => (
								<li key={index}>
									<span>
										<FormattedDate date={price.date} />
									</span>
									{price.price && (
										<RequestedNightPrice>
											<span>
												{addedAdditionalPersons ? t("from") + " " : ""}
												<FormattedCurrency value={price.price} />
											</span>
										</RequestedNightPrice>
									)}
								</li>
							))}
						</RequestedNightsList>
					))}
				</RequestedNightsGroups>
				{total > 0 && (
					<TotalPrice>
						<span>{t("Total price")}:</span>
						<span>
							{addedAdditionalPersons ? t("from") + " " : ""}
							<FormattedCurrency value={total} />
						</span>
					</TotalPrice>
				)}
			</Container>
		</CurrencyTypeProvider>
	);
};
